// The JS twin of src/Netcountable.App/E2EE/SodiumEnvelope.cs (NC1 envelope,
// FORMAT v2 — see EPOCH-KEYPAIR.md) plus the enrollment fingerprint and the
// device-list / member-list signing-bytes helpers from E2EEManager.cs /
// E2EEModels.cs. Byte-compatibility with C# is the contract —
// verify-vectors.js enforces it.
//
// v2 (2026-09-10): the symmetric per-epoch ADK is gone. Each RECORD gets a
// fresh random 32-byte key `k`; `k` is sealed (crypto_box_seal) to the epoch
// X25519 PUBLIC key and carried in the envelope header INSIDE the AAD (so
// both the AEAD tag and the device signature cover it). Only members hold
// the epoch SECRET (sealed to their identity keys in the ADK rows); an
// uploader needs just the epoch public key + its own device signing key and
// can never decrypt anything — including its own uploads. One `k` per
// record: an image and its thumbnail, or an event's four fields, share the
// same sealed key (with distinct nonces + `field` discriminators).
//
// Every function takes the ready `sodium` instance (browser: window.sodium
// after the two vendor <script> tags; Node: `await import('libsodium-wrappers-sumo')`).

import { canonicalize, canonicalizeBytes } from './jcs.js';

const MAGIC = new Uint8Array([0x4e, 0x43, 0x31]); // "NC1"
export const FORMAT_VERSION = 2;
const NONCE_BYTES = 24;  // crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
const SIG_BYTES = 64;    // crypto_sign_BYTES
const HASH_BYTES = 32;   // BLAKE2b-256
const RECORD_KEY_BYTES = 32;
const SEALED_KEY_BYTES = 32 + 16 + RECORD_KEY_BYTES; // crypto_box_SEALBYTES + key = 80
const MAX_HEADER_BYTES = 64 * 1024;

// ---- base64: standard alphabet WITH padding, matching C# Convert.To/FromBase64String ----
export function b64(sodium, bytes) {
  return sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
}
export function ub64(sodium, str) {
  return sodium.from_base64(str, sodium.base64_variants.ORIGINAL);
}

export function concatBytes(...arrays) {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}

// ---- epoch keypair (replaces the symmetric ADK) ---------------------------

/** Mint an epoch keypair: { epochPk, epochSk } (X25519, crypto_box_keypair). */
export function epochKeypair(sodium) {
  const kp = sodium.crypto_box_keypair();
  return { epochPk: kp.publicKey, epochSk: kp.privateKey };
}

/** Public half from the 32-byte epoch secret — members derive it after unsealing their ADK row. */
export function epochPkFromSk(sodium, epochSk) {
  return sodium.crypto_scalarmult_base(epochSk);
}

/**
 * One record key: fresh random `k` plus its copy sealed to the epoch public
 * key. Reuse the same object for every envelope of ONE record (image +
 * thumbnail; the four event fields); never across records.
 */
export function newRecordKey(sodium, epochPk) {
  const k = sodium.randombytes_buf(RECORD_KEY_BYTES);
  return { k, sealed: b64(sodium, sodium.crypto_box_seal(k, epochPk)) };
}

// ---- envelope -------------------------------------------------------------

/**
 * aad = { accountId, recordId, deviceId, epoch, field, key } — epoch must be
 * an integer; `key` is the base64 sealed record key (present in v2 always).
 */
export function aadBytes(aad) {
  return canonicalizeBytes({
    accountId: aad.accountId,
    recordId: aad.recordId,
    deviceId: aad.deviceId,
    epoch: aad.epoch,
    field: aad.field,
    key: aad.key,
  });
}

/** BLAKE2b-32(ciphertext ‖ canon(aad)) — the signed digest. */
function signingDigest(sodium, ciphertext, aadB) {
  return sodium.crypto_generichash(HASH_BYTES, concatBytes(ciphertext, aadB));
}

/**
 * Encrypt + sign into a binary NC1 v2 envelope (Uint8Array).
 * @param aad { accountId, recordId, deviceId, epoch, field } — `key` is filled in here
 * @param recordKey from newRecordKey(); shared by every envelope of this record
 * @param devSignSk this device's Ed25519 secret key (provenance)
 */
export function sealEnvelope(sodium, plaintext, aad, recordKey, devSignSk) {
  if (!recordKey?.k || !recordKey?.sealed) throw new Error('sealEnvelope needs a record key from newRecordKey()');
  const fullAad = {
    accountId: aad.accountId, recordId: aad.recordId, deviceId: aad.deviceId,
    epoch: aad.epoch, field: aad.field, key: recordKey.sealed,
  };
  const aadB = aadBytes(fullAad);
  const nonce = sodium.randombytes_buf(NONCE_BYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, aadB, null, nonce, recordKey.k);
  const sig = sodium.crypto_sign_detached(signingDigest(sodium, ciphertext, aadB), devSignSk);

  const header = {
    v: FORMAT_VERSION,
    aad: fullAad,
    nonce: b64(sodium, nonce),
    sig: b64(sodium, sig),
  };
  const headerBytes = new TextEncoder().encode(canonicalize(header));

  const lenBuf = new Uint8Array(4);
  new DataView(lenBuf.buffer).setUint32(0, headerBytes.length, /* littleEndian */ true);

  return concatBytes(MAGIC, lenBuf, headerBytes, ciphertext);
}

/** Seal a UTF-8 string payload, return standard base64 of the envelope. */
export function sealEnvelopeString(sodium, plaintextStr, aad, recordKey, devSignSk) {
  return b64(sodium, sealEnvelope(sodium, new TextEncoder().encode(plaintextStr), aad, recordKey, devSignSk));
}

/** Split an envelope into { v, aad, nonce, sig, ciphertext }. Throws on malformed input. */
export function parseEnvelope(sodium, envelope) {
  if (envelope.length < MAGIC.length + 4) throw new Error('Envelope too short.');
  if (envelope[0] !== MAGIC[0] || envelope[1] !== MAGIC[1] || envelope[2] !== MAGIC[2]) {
    throw new Error('Envelope magic mismatch — not an NC1 envelope.');
  }
  const headerLen = new DataView(envelope.buffer, envelope.byteOffset + MAGIC.length, 4).getUint32(0, true);
  if (headerLen > MAX_HEADER_BYTES || MAGIC.length + 4 + headerLen > envelope.length) {
    throw new Error('Envelope header length out of range.');
  }
  const headerJson = new TextDecoder().decode(envelope.subarray(MAGIC.length + 4, MAGIC.length + 4 + headerLen));
  const header = JSON.parse(headerJson);
  if (header.v !== FORMAT_VERSION) throw new Error(`Unsupported envelope version ${header.v}.`);
  if (!header.aad || typeof header.aad.key !== 'string') throw new Error('Envelope AAD is missing the sealed record key.');

  const nonce = ub64(sodium, header.nonce);
  const sig = ub64(sodium, header.sig);
  if (nonce.length !== NONCE_BYTES) throw new Error('Envelope nonce has wrong length.');
  if (sig.length !== SIG_BYTES) throw new Error('Envelope signature has wrong length.');
  if (ub64(sodium, header.aad.key).length !== SEALED_KEY_BYTES) throw new Error('Envelope sealed record key has wrong length.');

  return {
    v: header.v,
    aad: header.aad,
    nonce,
    sig,
    ciphertext: envelope.subarray(MAGIC.length + 4 + headerLen),
  };
}

export function parseEnvelopeString(sodium, envelopeB64) {
  return parseEnvelope(sodium, ub64(sodium, envelopeB64));
}

/** Provenance check — no decryption, no key needed. */
export function verifyEnvelope(sodium, parsed, devSignPk) {
  return sodium.crypto_sign_verify_detached(
    parsed.sig, signingDigest(sodium, parsed.ciphertext, aadBytes(parsed.aad)), devSignPk);
}

/** Recover the record key from the envelope with the epoch secret. Throws if not sealed to this epoch key. */
export function openRecordKey(sodium, parsed, epochPk, epochSk) {
  const k = sodium.crypto_box_seal_open(ub64(sodium, parsed.aad.key), epochPk, epochSk);
  if (k.length !== RECORD_KEY_BYTES) throw new Error('Record key has wrong length.');
  return k;
}

/** AEAD decrypt with the record key. Throws on tag/AAD mismatch. Returns Uint8Array. */
export function decryptEnvelope(sodium, parsed, recordKeyBytes) {
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null, parsed.ciphertext, aadBytes(parsed.aad), parsed.nonce, recordKeyBytes);
}

/**
 * Enrollment fingerprint (VIEWER links + safety numbers only — uploader
 * approvals are by device name in v2):
 * hex(first 8 bytes of BLAKE2b-16(transfer_pk ‖ dev_sign_pk ‖ dev_enc_pk)).
 * (BLAKE2b-16 then truncate, because crypto_generichash_BYTES_MIN is 16.)
 */
export function computeFingerprint(sodium, transferPk, devSignPk, devEncPk) {
  const hash = sodium.crypto_generichash(16, concatBytes(transferPk, devSignPk, devEncPk));
  return Array.from(hash.subarray(0, 8), (x) => x.toString(16).padStart(2, '0')).join('');
}

/** XXXX-XXXX-XXXX-XXXX display form. */
export function fingerprintDisplay(fingerprint) {
  return fingerprint.toUpperCase().match(/.{4}/g).join('-');
}

/**
 * §8.3 read-and-verify for one parsed envelope:
 *   1. resolve the author device's signing pk from the (already
 *      identity-verified) device list — unknown device = untrusted
 *   2. verify the device signature over BLAKE2b(ciphertext ‖ canon(aad))
 *   3. open the sealed record key with the epoch secret for the envelope's
 *      epoch, then AEAD-decrypt
 * Returns { ok, plaintext?, error?, revoked? } — never throws.
 *
 * @param ctx {
 *   devicePks: Map<deviceId, Uint8Array>,
 *   epochSks: Map<epochNumber, Uint8Array>,   // the caller's unsealed epoch secrets
 *   revokedDevices?: Set<deviceId>,
 *   epochPks?: Map<epochNumber, Uint8Array>,  // derived lazily from epochSks
 *   recordKeys?: Map<sealedKeyB64, Uint8Array> // per-load cache: one open per record, not per field
 * }
 */
export function openParsed(sodium, parsed, ctx) {
  const devPk = ctx.devicePks.get(parsed.aad.deviceId);
  if (!devPk) return { ok: false, error: `unknown device ${parsed.aad.deviceId} (not on the signed device list)` };
  if (!verifyEnvelope(sodium, parsed, devPk)) return { ok: false, error: 'BAD PROVENANCE SIGNATURE' };
  const epochSk = ctx.epochSks?.get(parsed.aad.epoch);
  if (!epochSk) return { ok: false, error: `no epoch key for epoch ${parsed.aad.epoch}` };
  try {
    if (!ctx.recordKeys) ctx.recordKeys = new Map();
    let k = ctx.recordKeys.get(parsed.aad.key);
    if (!k) {
      if (!ctx.epochPks) ctx.epochPks = new Map();
      let epochPk = ctx.epochPks.get(parsed.aad.epoch);
      if (!epochPk) { epochPk = epochPkFromSk(sodium, epochSk); ctx.epochPks.set(parsed.aad.epoch, epochPk); }
      k = openRecordKey(sodium, parsed, epochPk, epochSk);
      ctx.recordKeys.set(parsed.aad.key, k);
    }
    // Tombstoned author: historical records verify, but surface it — new
    // records from a revoked device are a red flag for the caller/UI.
    const revoked = ctx.revokedDevices?.has(parsed.aad.deviceId) ?? false;
    return { ok: true, plaintext: decryptEnvelope(sodium, parsed, k), revoked };
  } catch {
    return { ok: false, error: 'RECORD KEY OPEN OR AEAD DECRYPT FAILED (wrong epoch key, or tag/AAD mismatch)' };
  }
}

/**
 * The exact bytes the identity key signs for a device list:
 * canon({ devices: [{addedAt, devEncPk?, devSignPk, deviceId, revokedAt?}], userId, version }).
 * Mirror of C# SignedDeviceList.SignedBytes() / DeviceListEntry.ToJsonNode().
 */
export function deviceListSignedBytes(list) {
  return canonicalizeBytes({
    userId: list.userId,
    version: list.version,
    devices: list.devices.map((d) => deviceEntryForSigning(d)),
  });
}

/** Canonical shape of one device-list entry (shared by signing + re-signing paths). */
export function deviceEntryForSigning(d) {
  return {
    deviceId: d.deviceId,
    devSignPk: d.devSignPk,
    // v2 uploaders have no encryption key (nothing is ever sealed to a
    // device). Omitted entirely when absent so the canonical bytes match C#
    // DeviceListEntry.ToJsonNode(); viewer-era / legacy entries may carry it.
    ...(d.devEncPk != null ? { devEncPk: d.devEncPk } : {}),
    addedAt: d.addedAt,
    // Tombstone for revoked devices — omitted entirely when absent.
    ...(d.revokedAt != null ? { revokedAt: d.revokedAt } : {}),
  };
}

/**
 * The exact bytes the OWNER identity key signs for a member list (§7.3) —
 * v2 adds `epochPk`, the CURRENT epoch's public key (the only key an
 * uploader needs). Single home for this preimage: the service validator,
 * the webapp and C# all mirror it. `ownerSig` is never part of the bytes.
 */
export function memberListSignedBytes(list) {
  return canonicalizeBytes({
    accountId: list.accountId,
    version: list.version,
    keyEpoch: list.keyEpoch,
    epochPk: list.epochPk,
    members: list.members,
  });
}
